<script setup>
import { ref, defineProps } from 'vue'
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { ExclamationTriangleIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
  showDialog: Boolean,
  onConfirm: Function,
  onDeny: Function,
  onCustomAction: Function,
  title: String,
  description: String,
  backdropClickable: { type: Boolean, default: true },
  confirmText: { type: String, default: 'Delete' },
  denyText: { type: String, default: 'Cancel' },
  additionalButtons: { type: Array, default: () => [] },
  showDateInput: { type: Boolean, default: false },
  dateInputLabel: { type: String, default: 'Fecha' },
  dateRequired: { type: Boolean, default: false },
  minDate: { type: String, default: '' },
  dateValue: { type: String, default: '' },
})

const cancelButtonRef = ref(null)
const dateInputRef = ref(null)
const selectedDate = ref(props.dateValue || '')

const closeDialog = () => {
  if (props.backdropClickable) props.onDeny?.()
}

const confirm = () => props.onConfirm?.(selectedDate.value || null)
const deny = () => props.onDeny?.()
const handleCustomAction = (action) => props.onCustomAction?.(action, selectedDate.value || null)

const isActionDisabled = () => {
  if (!props.showDateInput || !props.dateRequired) return false
  return !selectedDate.value
}

const openNativeDatePicker = () => {
  const el = dateInputRef.value
  if (el && typeof el.showPicker === 'function') {
    try {
      el.showPicker()
    } catch (_) {
      // Algunos navegadores no permiten showPicker en todos los contextos.
    }
  }
}
</script>

<template>
  <TransitionRoot as="div" :show="props.showDialog">
    <Dialog as="div" class="relative z-50" :initialFocus="cancelButtonRef" @close="closeDialog">
      <!-- Overlay -->
      <TransitionChild as="div" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
        leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <!-- Content -->
      <div class="fixed inset-0 z-50 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
          <TransitionChild as="div" enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel
              class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all w-full max-w-lg sm:p-6">
              <div class="sm:flex sm:items-start">
                <div
                  class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <ExclamationTriangleIcon class="h-6 w-6 text-red-600" aria-hidden="true" />
                </div>
                <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <DialogTitle as="h3" class="text-lg font-medium leading-6 text-gray-900">
                    {{ props.title }}
                  </DialogTitle>
                  <div class="mt-2 text-sm text-gray-500" v-html="props.description"></div>
                </div>
              </div>

              <div v-if="props.showDateInput" class="mt-4">
                <label class="block text-sm font-medium text-gray-700 mb-1">{{ props.dateInputLabel }}</label>
                <input
                  ref="dateInputRef"
                  v-model="selectedDate"
                  type="date"
                  :min="props.minDate || null"
                  @click="openNativeDatePicker"
                  @focus="openNativeDatePicker"
                  class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div class="mt-5 sm:mt-4 sm:flex sm:items-center sm:justify-between">
                <!-- Botones adicionales -->
                <div class="flex space-x-2">
                  <template v-for="(button, index) in props.additionalButtons" :key="index">
                    <button type="button" :class="button.class" class="mb-2 sm:mb-0"
                      :disabled="isActionDisabled()"
                      @click="handleCustomAction(button.action)" @pointerup="handleCustomAction(button.action)">
                      {{ button.text }}
                    </button>
                  </template>
                </div>

                <!-- Botones principales -->
                <div class="mt-4 flex flex-col-reverse gap-2 sm:mt-0 sm:flex-row sm:justify-end sm:space-x-2">
                  <button ref="cancelButtonRef" data-cancel type="button"
                    class="inline-flex justify-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-200"
                    @click="deny" @pointerup="deny">
                    {{ props.denyText }}
                  </button>

                  <button type="button"
                    class="inline-flex justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    :disabled="isActionDisabled()"
                    @click="confirm" @pointerup="confirm">
                    {{ props.confirmText }}
                  </button>
                </div>


              </div>

            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<style scoped>
/* iOS touch fixes */
.fixed,
[role="dialog"] {
  -webkit-transform: translate3d(0, 0, 0);
  -webkit-overflow-scrolling: touch;
}

button,
[role="button"] {
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  cursor: pointer;
}

/* Asegura el stacking por encima de cualquier overlay de la app */
.dialog-container {
  position: relative;
  z-index: 9999;
}
</style>
